import { useEffect, useRef } from 'react';
import { contactPhone } from '@/mocks/homeData';
import FireworkCanvas from './FireworkCanvas';

export default function Hero() {
  const titleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (titleRef.current) {
        titleRef.current.classList.add('animate-fade-in-up');
        titleRef.current.classList.remove('opacity-0');
      }
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section id="hero" className="relative w-full h-screen min-h-[700px] overflow-hidden">
      {/* 배경 */}
      <div className="absolute inset-0">
        <img
          src="https://storage.readdy-site.link/project_files/5de00db6-0430-441c-b10d-847a16bb9b2c/68540bcd-a8fe-4f03-b466-ecbcbb8d360b_compressed_--.webp"
          alt="신상도역 이안 단지 전경"
          className="w-full h-full object-cover object-top"
          width={1920}
          height={1080}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/10 to-black/50" />
      </div>

      <FireworkCanvas />

      {/* 메인 콘텐츠 */}
      <div className="relative z-10 flex flex-col items-center h-full px-4 text-center pt-[100px] md:pt-[120px]">
        <div ref={titleRef} className="opacity-0 flex flex-col items-center w-full max-w-3xl">
          {/* 말풍선 배지 */}
          <div className="relative inline-block px-5 py-2 mb-5 bg-background-50 text-foreground-950 text-sm md:text-base font-bold rounded-full border-2 border-foreground-900 shadow-lg">
            상도역&신상도역(예정) 더블역세권
            <span className="absolute -bottom-[6px] left-1/2 -translate-x-1/2 w-3 h-3 bg-background-50 border-r-2 border-b-2 border-foreground-900 rotate-45" />
          </div>

          {/* 메인 타이틀 */}
          <h1 className="font-heading text-[2.8rem] md:text-[4rem] lg:text-7xl font-bold leading-[1.1] mb-5 text-balance">
            <span className="block text-background-50 drop-shadow-lg">
              상도 최초 10년
            </span>
            <span className="block text-primary-400 drop-shadow-lg">
              민간임대아파트
            </span>
          </h1>

          {/* 녹색 강조 배너 */}
          <div className="inline-flex items-center gap-1.5 px-5 py-3 md:px-7 md:py-3.5 rounded-lg bg-primary-700 text-background-50 text-base md:text-lg font-semibold shadow-xl mb-2">
            <span>특별 공급분</span>
            <span className="text-primary-300 font-bold">확정분양가</span>
            <span>적용!</span>
          </div>

          <p className="text-base md:text-lg text-background-50 font-semibold leading-relaxed mb-10 drop-shadow-lg" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
            총 459세대 · 서울 동작구 상도동
          </p>
        </div>

        {/* 하단 고정 CTA 영역 */}
        <div className="absolute bottom-6 md:bottom-10 left-0 right-0 px-4 md:px-8 w-full max-w-5xl mx-auto">
          {/* 좌우: 전화상담 + 원형 배지 */}
          <div className="flex items-end justify-between mb-4">
            {/* 전화상담 버튼 */}
            <a
              href={`tel:${contactPhone}`}
              className="flex flex-col items-center justify-center gap-0.5 px-4 py-3 md:px-6 md:py-4 rounded-xl bg-primary-600 text-background-50 text-center shadow-lg transition-all hover:bg-primary-700 hover:scale-105"
            >
              <span className="flex items-center gap-1.5 text-base md:text-lg font-bold whitespace-nowrap">
                <i className="ri-phone-line" />
                전화상담
              </span>
            </a>

            {/* 원형 배지 그룹 */}
            <div className="flex gap-2 md:gap-3">
              <div className="flex flex-col items-center justify-center w-[78px] h-[78px] md:w-[90px] md:h-[90px] rounded-full bg-primary-600 text-background-50 text-center shadow-lg border-2 border-primary-400">
                <span className="text-xs md:text-sm font-medium leading-tight">10년 후</span>
                <span className="text-sm md:text-base font-bold leading-tight">분양전환형</span>
              </div>
              <div className="flex flex-col items-center justify-center w-[78px] h-[78px] md:w-[90px] md:h-[90px] rounded-full bg-primary-600 text-background-50 text-center shadow-lg border-2 border-primary-400">
                <span className="text-xs md:text-sm font-medium leading-tight">임대가로</span>
                <span className="text-sm md:text-base font-bold leading-tight">내 집 마련</span>
              </div>
            </div>
          </div>

          {/* 하단 버튼 2개 */}
          <div className="flex gap-3">
            <a
              href="#reservation"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#reservation')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3.5 md:py-4 rounded-xl bg-foreground-900 text-background-50 text-base md:text-lg font-semibold whitespace-nowrap transition-all hover:bg-foreground-800 hover:scale-[1.02] shadow-lg"
            >
              <i className="ri-calendar-check-line" />
              방문예약
            </a>
            <a
              href="#pricing"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#pricing')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-3.5 md:py-4 rounded-xl bg-primary-500 text-background-50 text-base md:text-lg font-semibold whitespace-nowrap transition-all hover:bg-primary-600 hover:scale-[1.02] shadow-lg"
            >
              <i className="ri-calculator-line" />
              분양가확인
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}